import jsPDF from 'jspdf';
import {
  BillOfMaterialRecord,
  Details,
} from '../../../../types/BillOfMaterial';
import autoTable from 'jspdf-autotable';
import { RecordFile } from '../../../../types';
import { getTextWidth } from '../../../../lib/utils';

const columnHeaders = [
  'Product Code',
  'Product',
  'Size',
  'Brand',
  'UoM',
  'Set No',
  'SKU',
  'Quantity',
];
export function generatePDFPrint({
  data,
  attachments,
  mode = 'download',
}: {
  data?: BillOfMaterialRecord;
  attachments?: RecordFile[];
  mode?: 'print' | 'download';
}) {
  const groupedDetails = groupDetailsByProcessGroup(data?.details);
  const doc = new jsPDF('p', 'px', 'a4');

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // jsPDF.addImage throws on a plain URL path (needs a data URL / Image element),
  // and that exception was aborting the ENTIRE pdf -> blank print preview.
  // Guard it so a missing/unloadable logo never blanks the document.
  try {
    doc.addImage('/mmd/mmd.png', 'PNG', 10, 10, 30, 15);
  } catch (e) {
    /* logo optional */
  }
  const fontSize = 14;
  const fontType = 'helvetica';

  doc.setFont(fontType, 'bold');
  doc.setFontSize(fontSize);

  doc.text(['ASIA CERAMIC CORPORATION'], 45, 20);
  doc.text(
    ['Bill of Material'],
    pageWidth - getTextWidth(doc, 'Bill of Material', fontSize, fontType) - 10,
    20,
  );

  doc.setFontSize(8);
  doc.text(['Bill of Material:' + (data?.code || '')], 14, 31);
  doc.text(['Purchase Order: ' + (data?.proderno || '--')], 14, 39);
  doc.text(['Buyer Code: ' + (data?.buyercode || '--')], 14, 47);

  const dateEntry = 'Date: ' + (data?.dateEntry || '');
  doc.text(
    [dateEntry],
    pageWidth - getTextWidth(doc, dateEntry, 8, fontType) - 10,
    31,
  );

  const dateShipment = 'Shipment Date: ' + (data?.dateshipment || '');
  doc.text(
    [dateShipment],
    pageWidth - getTextWidth(doc, dateShipment, 8, fontType) - 10,
    39,
  );

  const requiredDate = 'Required Date: ' + (data?.requiredDate || '');
  doc.text(
    [requiredDate],
    pageWidth - getTextWidth(doc, requiredDate, 8, fontType) - 10,
    47,
  );

  const groupedDetailsKeys = Object.keys(groupedDetails).sort();

  let currentY = 60;
  groupedDetailsKeys.forEach((processGroup) => {
    const sectionHeightEstimate = 40; // Estimated height for each section (adjust as necessary)

    // Check if there’s enough space for this section
    if (currentY + sectionHeightEstimate > pageHeight) {
      doc.addPage();
      currentY = 20; // Reset Y position on a new page
    }

    doc.setFontSize(10);
    doc.text(`${processGroup}`, 10, currentY);
    currentY += 10;

    const detailsInGroup = groupedDetails[processGroup];
    const tableData = detailsInGroup.map((detail: any) => [
      detail.product?.code ?? detail.materialCode,
      detail.product?.description ?? detail.materialName,
      detail.size,
      detail.brand,
      detail.defaultUom?.description ?? detail.unit,
      detail.setPackingNo,
      detail.sku,
      detail.quantity,
    ]);

    tableData.sort((a: any, b: any) => (a[0] || '').localeCompare(b[0] || ''));
    doc.setFontSize(8);
    tableData.unshift(columnHeaders);

    autoTable(doc, {
      startY: currentY + 5,
      body: tableData,
      theme: 'plain',
      styles: {
        fontSize: 10,
        textColor: [0, 0, 0],
        cellPadding: 3,
      },
      columnStyles: {
        2: { cellWidth: 'auto' },
      },
      didDrawPage: (d) => {
        if (d.cursor && currentY != null) {
          currentY = d.cursor?.y + 20;
        }
      },
    });
  });

  const footerSpaceRequired = 40;
  const spaceLeftOnPage = pageHeight - currentY;
  if (spaceLeftOnPage < footerSpaceRequired) {
    doc.addPage();
    currentY = 10;
  } else {
    currentY += 30;
  }

  doc.setFontSize(10);
  // doc.text('Prepared By:', 20, currentY);
  // doc.text(data?.preparedBy || '', 20, currentY + 15);
  // doc.text('___________________________', 20, currentY + 18);
  // doc.text('Purchasing Staff', 20, currentY + 28);

  doc.text('Approved By:', pageWidth / 2 + 20, currentY);
  doc.text(data?.approvedBy || '', pageWidth / 2 + 20, currentY + 15);
  doc.text('___________________________', pageWidth / 2 + 20, currentY + 18);
  doc.text('Material Planner', pageWidth / 2 + 20, currentY + 28);

  attachments?.forEach((imageUrl, index) => {
    if (index > 0) {
      doc.addPage();
    }
    const pageWidth = doc.internal.pageSize.getWidth();
    const imageWidth = pageWidth - 50;

    const pageHeight = doc.internal.pageSize.getHeight();
    const maxImageHeight = pageHeight - 30;

    try {
      doc.addImage(imageUrl['code'], 'JPEG', 10, 10, imageWidth, maxImageHeight);
    } catch (e) {
      /* attachment image optional */
    }
  });

  if (mode === 'print') {
    printPdfViaFrame(doc);
  } else if (mode === 'download') {
    doc.save(`bill-of-material-${data?.code}`);
  }

  // const tableData = groupedDetailsKeys.reduce(
  //   (result: any, processGroup, index) => {
  //     // Add the column headers only for the first group
  //     if (index === 0) {
  //       result.push(columnHeaders);
  //     }
  //
  //     // Add a subheader row for the product group
  //     result.push([
  //       {
  //         content: processGroup,
  //         colSpan: columnHeaders.length,
  //         styles: { fontStyle: 'bold' },
  //       },
  //     ]);
  //
  //     // Add data rows for the product group
  //     result.push(
  //       ...groupedDetails[processGroup].map((item: any) => [
  //         item.product ? item.product.code : '',
  //         item.product ? item.product.description : '',
  //         item.size,
  //         item.brand,
  //         item.purchaseUom ? item.purchaseUom.description : '',
  //         item.setPackingNo,
  //         item.sku,
  //         item.quantity,
  //       ]),
  //     );
  //
  //     // Add an empty row for spacing
  //     result.push(['']);
  //
  //     return result;
  //   },
  //   [],
  // );

  // let startYForTable = 60;

  // autoTable(doc, {
  //   body: tableData,
  //   theme: 'plain',
  //   styles: {
  //     fontSize: 10,
  //     textColor: [0, 0, 0],
  //     cellPadding: 3,
  //   },
  //   columnStyles: {
  //     ...Object.keys(columnHeaders).reduce(
  //       (a: any, c) => (
  //         (a[c] = {
  //           halign: 'left',
  //           cellHeight: c === 'Size' ? '1000' : 'auto',
  //           cellWidth: c === 'Size' ? 'wrap' : 'auto',
  //         }),
  //         a
  //       ),
  //       {},
  //     ),
  //   },
  //   margin: { top: startYForTable }, // Set margin to position the table
  //   didDrawPage: (data) => {
  //     // If the table is not on the first page, adjust startY to 0
  //     if (data.pageNumber > 1) {
  //       startYForTable = 0;
  //     }
  //   },
  // });
}

function groupDetailsByProcessGroup(details: Details[] | undefined) {
  return details?.reduce((result: any, detail) => {
    const processGroup = detail.processGroup;
    if (!result[processGroup]) {
      result[processGroup] = [];
    }
    result[processGroup].push(detail);
    return result;
  }, {});
}

// Print a jsPDF via a single reusable, off-screen iframe.
// - one iframe, id'd and removed before each print, so nothing piles up on
//   repeat clicks (which used to make Brave offer to print the web page);
// - the iframe is RENDERED (0-size, off-screen) NOT display:none, otherwise
//   contentWindow.print() falls through to the parent window;
// - print() is called on the iframe after it loads (+ a small delay so the
//   PDF viewer is ready), with focus() so the dialog targets the PDF.
export function printPdfViaFrame(doc: jsPDF) {
  const url = doc.output('bloburl') as unknown as string;
  const prev = document.getElementById('__mmd_print_frame');
  if (prev) prev.remove();
  const frame = document.createElement('iframe');
  frame.id = '__mmd_print_frame';
  frame.setAttribute(
    'style',
    'position:fixed;left:-9999px;top:0;width:0;height:0;border:0;',
  );
  frame.onload = () => {
    setTimeout(() => {
      try {
        frame.contentWindow?.focus();
        frame.contentWindow?.print();
      } catch (e) {
        /* ignore */
      }
    }, 250);
  };
  frame.src = url;
  document.body.appendChild(frame);
}
